import classes from "./FormEducation.module.css";
import FormEducationProfessional from "./FormEducationProfessional";
import FormEducationComplete from "./FormEducationComplete";
import SaveUnfinishedButton from "../Buttons/SaveUnfinishedButton/SaveUnfinishedButton";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import { useTranslation } from "react-i18next";
import FormEducationSelect from "./FormEducationSelect";
import { useEffect, useState } from "react";
import { useUserContext } from "../../../contexts/UserContext";
import { getSelectableEducationLevels } from "../../../scripts/dataHandlers";
import { EducationLevel } from "../../../contexts/interfaces";
import FileDropZone from "../../Assets/FileDropZone/FileDropZone";
import { useErrorContext } from "../../../contexts/ErrorContext";

interface Props {
  formSubmitHandler: (
    e: React.SyntheticEvent<HTMLFormElement> | undefined,
    step: string
  ) => void;
  setLoading: (val: boolean) => void;
}

const FormEducation = ({ formSubmitHandler, setLoading }: Props) => {
  const { currentData, setCurrentData } = useCurrentDataContext();

  const { user } = useUserContext();

  const { handleError } = useErrorContext();

  const [educationLevels, setEducationLevels] = useState<EducationLevel[]>([]);

  // Set translation
  const { t } = useTranslation();

  useEffect(() => {
    const getSelectData = async () => {
      const res = user && (await getSelectableEducationLevels(user.id));
      if (res?.status === 200) {
        setEducationLevels([...res.data.result]);
      } else {
        handleError(res, "GetSelectableEducationLevelsFailed");
      }
    };

    user && getSelectData();
  }, [user]);

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    setLoading(true);
    // Call for form submitHandler, which updates data to database
    formSubmitHandler(e, "permits");
  };

  const clickProfessional = () => {
    // Find default professional education level to be used, when user changed isProfessional tab
    const defaultEducationLevel =
      educationLevels &&
      educationLevels.find((education) => education.isProfessional === true);
    // Update currentData with new values, while clearing student side
    currentData &&
      setCurrentData({
        ...currentData,
        isProfessional: true,
        educationId: defaultEducationLevel?.id,
        educationLevel: defaultEducationLevel?.title,
        records:
          currentData?.records && currentData.records.length > 0
            ? currentData.records.filter((record) => record.type !== "study")
            : [],
      });
  };

  const clickStudent = () => {
    // Find default student education level to be used, when user changed isProfessional tab
    const defaultEducationLevel =
      educationLevels &&
      educationLevels.find((education) => education.isProfessional === false);

    // Update currentData with new values, while clearing professional side
    currentData &&
      setCurrentData({
        ...currentData,
        isProfessional: false,
        registerNumber: null,
        isRegistered: false,
        educationId: defaultEducationLevel?.id,
        educationLevel: defaultEducationLevel?.title,
        records:
          currentData?.records && currentData.records.length > 0
            ? currentData.records.filter(
                (record) => record.type !== "education"
              )
            : [],
      });
  };

  // Function which checks disabled status for submit button
  const checkDisabled = () => {
    let isDisabled;

    if (currentData?.isProfessional) {
      // ** Professional
      // If fields are empty or register number is given but also "no register number"
      // is checked, it will disable submit button
      if (
        currentData.educationLevel === "" ||
        currentData.educationId === null ||
        (currentData.educationId === 2 &&
          currentData.records &&
          currentData.records.filter((record) => record.type === "education")
            .length === 0)
      ) {
        isDisabled = true;
      } else if (
        currentData.registerNumber === null &&
        currentData.isRegistered === true
      ) {
        isDisabled = true;
      } else if (
        currentData.registerNumber !== null &&
        currentData.isRegistered === false
      ) {
        isDisabled = true;
      } else if (
        currentData.isRegistered === false &&
        currentData.records &&
        currentData.records.filter((record) => record.type === "education")
          .length === 0
      ) {
        isDisabled = true;
      } else {
        isDisabled = false;
      }
    } else {
      // ** Student
      // If educationLevel selection is not done or file is not given, it will disable submit button
      if (
        currentData?.educationLevel === "" ||
        (currentData?.records &&
          currentData.records.filter((record) => record.type === "study")
            .length === 0) ||
        currentData?.educationId === null
      ) {
        isDisabled = true;
      } else {
        isDisabled = false;
      }
    }
    return isDisabled;
  };

  if (currentData?.formStatus === "education") {
    return (
      <div className={classes.formEducationContainer}>
        <div className={classes.formEducationTitle}>
          {`1 ${t("formEducation.mainTitle")}`}
        </div>
        <form
          className={classes.formEducationInnerContainer}
          onSubmit={(e) => handleSubmit(e)}
        >
          <div>{t("formEducation.professionalOrStudent")}</div>
          <div className={classes.formEducationButtonWrapper}>
            <div className={classes.formEducationRow}>
              <button
                className={
                  currentData?.isProfessional
                    ? classes.formEducationButtonActive
                    : classes.formEducationButton
                }
                onClick={() => clickProfessional()}
                type="button"
              >
                {t("formEducation.professional")}
              </button>
              <button
                className={
                  currentData?.isProfessional
                    ? classes.formEducationButton
                    : classes.formEducationButtonActive
                }
                onClick={() => clickStudent()}
                type="button"
              >
                {t("formEducation.student")}
              </button>
            </div>
          </div>

          {educationLevels.length > 0 && (
            <FormEducationSelect
              options={[
                ...(currentData?.isProfessional
                  ? educationLevels.filter(
                      (education) => education.isProfessional
                    )
                  : educationLevels.filter(
                      (education) => !education.isProfessional
                    )),
              ]}
            />
          )}

          {currentData?.isProfessional ? (
            <FormEducationProfessional />
          ) : (
            <>
              <div className={classes.askAttachmentText}>
                {t("formEducation.askForAttachmentForStudent")}
              </div>
              <FileDropZone fileType={"study"} />
            </>
          )}

          <button
            className="formContinueButton"
            type="submit"
            disabled={checkDisabled()}
          >
            {t("formEducation.continueButton")}
          </button>

          <SaveUnfinishedButton
            formSubmitHandler={formSubmitHandler}
            step={"education"}
          />
        </form>
      </div>
    );
  } else {
    //if (!checkDisabled()) {
    return (
      <FormEducationComplete
        formSubmitHandler={formSubmitHandler}
        setLoading={setLoading}
      />
    );
  } /*else {
        return null
    } */
};

export default FormEducation;
